import React from 'react';
import {FACTIONS} from '../../../constants/factions';
import {MaskedIcon} from "../../coloredIcons/MaskedIcon/MaskedIcon";
import RulesSectionLink, {RULES_ANCHOR_IDS} from "../RulesSectionLink/RulesSectionLink";
import {RULES_SECTION_IDS} from "../../Modals/RulesModal/rulesSectionIds";
import {GIANTS_D20_SAME_TYPE_UNIT_LIMIT} from "../../../configs/factions/giants";
import {CELECTIALS_MELEE_DAMAGE_DICE_COUNT} from "../../../configs/factions/celectials";
import {MELEE_COUNTED_DAMAGE_DICE_COUNT} from "../../../configs/damage";
import {
  SUCCUBES_SEDUCTION_COST,
  SUCCUBES_SEDUCTION_RANGE,
  SUCCUBES_SEDUCTION_SUCCESS_THRESHOLD
} from "../../../configs/factions/succubes";
import {
  GOBLINS_ABILITY_RADIUS,
  GOBLINS_ABILITY_SUCCESS_THRESHOLD,
  GOBLINS_FORTRESS_DAMAGE_PENALTY,
  GOBLINS_MIN_UNIT_SIZE_FOR_ABILITY,
  GOBLINS_TRAP_REMOVAL_REWARD,
  GOBLINS_TRAP_SETUP_COST
} from "../../../configs/factions/goblins";

/**
 * Подробные правила фракций для раздела правил «Фракции» (блок внизу
 * страницы). Оформление — как у блока «Великаны» на странице «Фаза
 * Снабжения»: абзацы обычного текста со значками интерфейса.
 *
 * Реестр: id фракции -> компонент правил. Фракция без записи в реестре
 * просто не получает блок (см. FactionsRules).
 */

// Подзаголовок абзаца (первое предложение) — цветом самой фракции. Цвет
// приходит переменной --faction-text-color с блока правил фракции, чтобы не
// протаскивать его пропом в каждый компонент (см. FactionsRules)
const Subheading = ({children}) => (
  <b className={'rules-page_faction-rules-subheading'}>{children}</b>
);

const statIcon = (src, alt) => (
  <img
    className={'policy-description-icon policy-description-icon-margin-right-left'}
    src={src}
    alt={alt}
    title={alt}
  />
);

const buildingIcon = (src, alt) => (
  <img
    className={'policy-description-icon-building policy-description-icon-building-margin-right-left'}
    src={src}
    alt={alt}
    title={alt}
  />
);

const unitIcon = (src, alt) => (
  <img
    className={'policy-description-icon-unit policy-description-icon-unit-margin-right-left'}
    src={src}
    alt={alt}
    title={alt}
  />
);

const coins = statIcon('/img/icons/coins.png', 'Монеты');
const supply = statIcon('/img/icons/supply.png', 'Снабжение');
const damage = statIcon('/img/icons/damage.png', 'Урон');
const size = statIcon('/img/icons/unit size.png', 'Размер отряда');
const winPoints = statIcon('/img/icons/win points.png', 'Победные очки');
const policy = statIcon('/img/icons/governance.png', 'Политика');
const mineExploration = statIcon('/img/icons/mine_exploration.9e85865b.png', 'Разведка рудника');

const shooting = statIcon('/img/icons/shooting.png', 'Стрельба');
const distance = statIcon('/img/icons/distance.png', 'Дальность');
const dice = statIcon('/img/icons/dice.png', 'Бросок кости');
const undo = statIcon('/img/icons/return.png', 'Отмена действия');
// Иконка полета — белый силуэт: на светлой странице правил перекрашиваем маской
const flight = (
  <MaskedIcon
    className={'policy-description-icon policy-description-icon-margin-right-left'}
    src={'/img/icons/fly.png'}
    alt={'Полет'}
    title={'Полет'}
  />
);
const elementEarth = statIcon('/img/icons/element_earth.png', 'Земля');
const elementFire = statIcon('/img/icons/element_fire.png', 'Огонь');
const elementWheat = statIcon('/img/icons/element_wheat.png', 'Пшеница');

const farm = buildingIcon('/img/hexes/farm.699e6dfc.png', 'Ферма');
const barracks = buildingIcon('/img/hexes/barracks.efab12f7.png', 'Казарма');
const mountain = buildingIcon('/img/hexes/mountains/mountain_rock_1.862dc5c3.png', 'Гора');
const mine = buildingIcon('/img/hexes/mine.ecae90cd.png', 'Рудник');
const superMine = buildingIcon('/img/hexes/super_mine.7a4a81f7.png', 'Великий рудник');
const road = buildingIcon('/img/hexes/road.1f281346.png', 'Дорога');
const quarter = buildingIcon('/img/hexes/quarter.f69660fa.png', 'Квартал');
const centralQuarter = buildingIcon('/img/hexes/quarter_base.4ec61fc0.png', 'Центральный квартал');
const specialBuilding = buildingIcon('/img/hexes/special buildings/special building.c496c562.png', 'Специальное строение');
const guild = buildingIcon('/img/hexes/special buildings/guild.ee26d2b8.png', 'Гильдия');
const temple = buildingIcon('/img/hexes/special buildings/temple.17c08b3d.png', 'Храм');
const militaryCamp = buildingIcon('/img/hexes/special buildings/military camp.1216e2cd.png', 'Военный лагерь');

const unit = unitIcon('/img/icons/unit d6 friend.png', 'Отряд');
const unitEnemy = unitIcon('/img/icons/unit d6 enemy.png', 'Отряд противника');
const catapult = unitIcon('/img/icons/unit d4 friend.png', 'Катапульта');
const eliteUnit = unitIcon('/img/icons/unit d20 friend.png', 'Элитная пехота');
const heavyInfantry = unitIcon('/img/icons/unit d10 friend.png', 'Тяжёлая пехота');
const lightCavalry = unitIcon('/img/icons/unit d8 friend.png', 'Лёгкая кавалерия');
const heavyCavalry = unitIcon('/img/icons/unit d12 friend.png', 'Тяжёлая кавалерия');

const HumansRules = () => (
  <>
    <p>
      <Subheading>Скидка на политики.</Subheading> Политика стоит столько монет {coins}, какова её позиция на рынке (1-я — 1
      монета, 6-я — 6 монет), а людям каждая политика обходится на <b>3 монеты дешевле</b>. Первые три позиции
      рынка достаются им <b>бесплатно</b>, доплаты сверх цены не бывает никогда.
    </p>
    <p>
      <Subheading>Мгновенная активация.</Subheading> У остальных фракций купленная политика {policy} вступает в силу только со
      следующего раунда (кроме самой первой политики в партии). Политика людей начинает действовать <b>сразу
      в момент покупки</b>, даже если она заменяет уже работающую, — менять курс можно посреди своего хода.
    </p>
  </>
);

const OrcsRules = () => (
  <>
    <p>
      <Subheading>Повышенная живучесть.</Subheading> Полученный урон {damage} снимает у орочьих отрядов меньше размера {size},
      чем у всех остальных: за каждые <b>3 единицы урона</b> отряд теряет <b>2 размера</b>, а из остатка
      2 единицы урона снимают 1 размер, и 1 единица урона не снимает ничего.
    </p>
    <p>
      Итоговая таблица потерь: 1 урона — 0 размера, 2 урона — 1, 3 урона — 2, 4 урона — 2, 5 урона — 3,
      6 урона — 4.
    </p>
    <p>
      Правило действует и в ближнем бою, и при обстреле, для всех типов отрядов. Если орочий отряд стоит в
      целой крепости, снижения складываются: сначала крепость пропускает внутрь только треть урона, и уже этот
      остаток пересчитывается по орочьей таблице.
    </p>
  </>
);

const DwarvesRules = () => (
  <>
    <p>
      <Subheading>Разведка рудников.</Subheading> В свою <span className={'text-construction'}>фазу Строительства</span> гномы
      могут разведать рудник {mineExploration} — это <b>1 действие строительства</b> и <b>3 монеты</b> {coins}.
      Разведывать можно пустую ячейку (или ячейку с дорогой {road}), смежную кварталу своего города и <b>не
      смежную</b> другому руднику.
    </p>
    <p>
      Результат определяет бросок кости: <b>1–4</b> — обычный рудник {mine} (+1 монета дохода),
      <b> 5–6</b> — великий рудник {superMine} (+2 монеты дохода). Бросок необратим: отменить разведку и
      сделанные до неё постройки уже нельзя.
    </p>
    <p>
      <Subheading>Бой у гор.</Subheading> Если гномий отряд стоит <b>смежно горе</b> {mountain}, он получает <b>+2</b> к урону
      {damage} в ближнем бою. Бонус даётся один раз — две или три соседние горы его не удваивают. Как и любые
      бонусы урона, он не может превысить размер {size} самого отряда.
    </p>
    <p>
      <Subheading>Нет кавалерии.</Subheading> Гномам недоступны лёгкая и тяжёлая кавалерия: их нельзя ни нанять, ни получить
      улучшением отряда. В распоряжении гномов пехота, тяжёлая пехота, элитная пехота {eliteUnit} и
      катапульты {catapult}.
    </p>
  </>
);

const VentoryRules = () => (
  <>
    <p>
      <Subheading>Доход гильдий и храмов.</Subheading> Каждая гильдия {guild} Вентори приносит на <b>1 монету</b> {coins} больше
      обычного, а каждый храм {temple} — на <b>1 победное очко</b> {winPoints} больше. Бонус считается для
      каждого строения отдельно и складывается с бонусами политик; заблокированное врагом строение дохода не
      даёт, как и у всех.
    </p>
    <p>
      <Subheading>Дешёвые дороги.</Subheading> Дорога {road} обходится Вентори в <b>0 монет</b>. Действие строительства она при
      этом расходует обычным образом — бесплатны только сами монеты.
    </p>
  </>
);

const LeprechaunsRules = () => (
  <>
    <p>
      <Subheading>Увеличенный базовый доход.</Subheading> В конце каждого раунда лепреконы получают на <b>1 монету</b> {coins}
      больше базового дохода. Как и сам базовый доход, эта монета начисляется, только пока у игрока есть хотя бы
      1 город.
    </p>
    <p>
      <Subheading>Кража монет.</Subheading> На <span className={'text-war'}>этапе Перемещения</span> отряд {unit} лепреконов
      может <b>вместо своего перемещения</b> обокрасть любого противника — расстояние и соседство не важны,
      красть можно с любой точки поля.
    </p>
    <p>
      Размер добычи зависит от размера {size} отряда: <b>1 монета за каждые полные 5 размера</b> (размер 5–9 —
      1 монета, 10–14 — 2 монеты, 15–19 — 3 монеты). Больше, чем есть у жертвы, украсть нельзя. Каждый отряд
      может воровать <b>не чаще одного раза за фазу</b>, и отряд, уже сделавший перемещение, украсть в этой
      фазе не сможет.
    </p>
  </>
);

const DemonsRules = () => (
  <>
    <p>
      <Subheading>Аура страха.</Subheading> Рядом с демонами отряды противника бьют слабее: за <b>каждые полные 5 суммарного
      размера</b> {size} демонских отрядов <b>в радиусе 3 ячеек</b> урон {damage} вражеского отряда
      уменьшается на <b>1</b>.
    </p>
    <p>
      В <b>ближнем бою</b> штраф получает тот вражеский отряд, рядом с которым стоит хотя бы один отряд
      демонов, — а вот считаются все демоны в радиусе 3 ячеек от него, не только соседние. При <b>обстреле</b>
      соседство не нужно вовсе: стрелку, целящемуся в демонов, штраф считают по демонам в радиусе 3 ячеек
      уже <b>от него самого</b>.
    </p>
    <p>
      Штрафы разных игроков-демонов складываются, но урон отряда не может опуститься ниже нуля.
    </p>
  </>
);

const BeastmenRules = () => (
  <>
    <p>
      <Subheading>Ускоренный рост размера.</Subheading> На своей рабочей казарме {barracks} отряд {unit} зверолюдей получает от
      базового снабжения {supply} <b>+3</b> к размеру {size} вместо обычных +2, а если рядом с казармой
      работает военный лагерь {militaryCamp} — <b>+5</b> вместо +4. Вне казармы прирост обычный: +1.
      Катапульты {catapult} растут как у всех остальных фракций.
    </p>
    <p>
      <Subheading>Очки за добычу.</Subheading> За <b>каждый</b> уничтоженный отряд противника — в ближнем бою или обстрелом —
      зверолюди получают <b>+1 победное очко</b> {winPoints} сверх обычной награды за отряд этого типа.
    </p>
  </>
);

const ElementalsRules = () => (
  <>
    <p>
      <Subheading>Больше действий.</Subheading> В каждой своей <span className={'text-construction'}>фазе Строительства</span>
      {' '}элементали получают <b>+2 действия</b> сверх обычного числа.
    </p>
    <p>
      <Subheading>Дешёвые центральные кварталы.</Subheading> Центральный квартал {centralQuarter} — основание нового города —
      обходится элементалям в <b>2 монеты</b> {coins} вместо 4.
    </p>
    <p>
      <Subheading>Жетоны стихий.</Subheading> Строить элементали могут только тем, что дали стихии. В начале своей фазы
      Строительства игрок вслепую тянет из мешка <b>столько жетонов, сколько у него действий</b>. В мешке
      всего <b>9 жетонов</b>: 4 земли {elementEarth}, 3 огня {elementFire} и 2 пшеницы {elementWheat}.
    </p>
    <p>
      Каждая постройка тратит жетон своей стихии: <b>земля</b> — кварталы {quarter}, центральные кварталы и
      все специальные строения {specialBuilding}; <b>огонь</b> — казармы {barracks}; <b>пшеница</b> —
      фермы {farm}. Дороги {road} жетонов не требуют вовсе. Отмена постройки возвращает жетон обратно, а в
      конце хода неиспользованные жетоны сбрасываются — в следующий ход мешок тянется заново.
    </p>
  </>
);

const ElvesRules = () => (
  <>
    <p>
      <Subheading>Все отряды стреляют.</Subheading> На <span className={'text-war'}>этапе Стрельбы</span> у эльфов стреляет
      любой отряд {unit}, а не только катапульты. Дальность выстрела — <b>4 ячейки</b> {shooting}
      (у катапульты {catapult} по-прежнему 5). Как и всем, стрелять мешает вражеский отряд на соседней
      ячейке, а на линии выстрела — горы {mountain} и достроенные крепости.
    </p>
    <p>
      Эльфийские стрелки бьют <b>только по отрядам</b>: строения от их выстрелов не страдают — для осады
      по-прежнему нужны катапульты. Урон {damage} считается по формуле ближнего боя, но <b>вдвое меньше</b>:
      размер отряда плюс обе кости урона, поделённые пополам с округлением вверх. Зато <b>штрафа за
      перемещение нет</b> — эльф стреляет одинаково и с места, и после хода.
    </p>
    <p>
      <Subheading>Замедленный рост на казармах.</Subheading> Расплата за стрельбу — снабжение {supply}: на своей рабочей
      казарме {barracks} отряд эльфов растёт всего на <b>+1</b> вместо +2, а с военным лагерем
      {militaryCamp} — на <b>+2</b> вместо +4. Вне казармы прирост обычный: +1. Катапульт эльфов это
      замедление не касается.
    </p>
  </>
);

const EnlightenedRules = () => (
  <>
    <p>
      <Subheading>Бонус к начальному золоту.</Subheading> Просветлённые начинают партию с <b>+5 монетами</b> {coins} сверх
      обычного стартового запаса.
    </p>
    <p>
      <Subheading>Обмен золота на очки 1 к 1.</Subheading> При подведении итогов партии оставшиеся монеты превращаются в
      победные очки {winPoints}: у всех фракций <b>2 монеты дают 1 очко</b>, а у просветлённых —
      <b> 1 монета даёт 1 очко</b>. Обменивать ничего не нужно, пересчёт происходит автоматически в конце
      игры, так что накопленная казна для просветлённых стоит ровно вдвое дороже. Но если в конце раунда у
      игрока не осталось ни одного города, казна сгорает, как и у любой фракции.
    </p>
  </>
);

const GiantsRules = () => (
  <>
    <p>
      <Subheading>Только Великаны.</Subheading> Единственный доступный фракции отряд — <b>Великан</b> {eliteUnit} (Элитная
      пехота): та же цена найма и та же скорость, но <b>предельный размер {size} практически не ограничен</b>,
      и растить одного гиганта можно бесконечно. Одновременно на поле их может быть <b>{GIANTS_D20_SAME_TYPE_UNIT_LIMIT}</b> — стандартный лимит для отрядов одного типа.
      <br/>Улучшать отряд не во что, а противник за уничтожение Великана
      получает <b>5 победных очков</b> {winPoints} вместо обычных 4.
    </p>
    <p>
      <Subheading>Неограниченный рост.</Subheading> Великаны получают базовое снабжение {supply} сколько угодно раз за один и
      тот же отряд за ход — каждая единица увеличивает размер как обычно (+1, а в казарме {barracks} +2).
      Бонус военного лагеря {militaryCamp} (+4 в казарме) при этом действует <b>только на первую</b> единицу
      снабжения отряда за ход.
    </p>
    <p>
      <Subheading>Дорогое дополнительное снабжение.</Subheading> Дополнительное снабжение стоит великанам не 1, а <b>4 единицы
      снабжения</b> {supply} и по-прежнему даёт не рост размера, а один из обычных бонусов (переброс костей
      урона, дополнительное перемещение или манёвр) — не более 1 раза за отряд за ход, как и у остальных
      фракций. Уже розданное снабжение можно пересобрать: 4 базовых единицы обмениваются на 1 дополнительную
      и обратно.
    </p>
    <p>
      К отрядам-великанам применяются те же бонусы от политик, какие применяются к отрядам типа Элитной пехоты{eliteUnit}.
    </p>
  </>
);

const InsectoidsRules = () => (
  <>
    <p>
      <Subheading>Рост без снабжения.</Subheading> В начале своей <span className={'text-supply'}>фазы Снабжения</span> каждый
      отряд {unit} инсектоидов <b>сам получает базовое снабжение</b> {supply} — бесплатно, не расходуя фермы
      {farm} и не занимая раздачу. Поэтому инсектоиды никогда не голодают: терять размер {size} без снабжения
      им не грозит, даже если ферм не осталось вовсе. Отменить это снабжение нельзя.
    </p>
    <p>
      Расплата за это — <b>пустые ячейки</b>: если отряд стоит на ячейке без построек, его бесплатное
      снабжение <b>не увеличивает размер</b>. Рост идёт только на занятой ячейке — на своей рабочей казарме
      {barracks} +2, на любой другой постройке +1.
    </p>
    <p>
      <Subheading>Первое снабжение с фермы — дополнительное.</Subheading> Раз базовое отряд уже получил, первая же единица
      снабжения {supply} с настоящей фермы становится для него <b>дополнительным снабжением</b> и даёт бонус
      фазы Войны (переброс костей урона, дополнительное перемещение или манёвр) — не более 1 раза за отряд за
      ход.
    </p>
  </>
);

const RatmenRules = () => (
  <>
    <p>
      <Subheading>Найм за снабжение.</Subheading> В свой ход в <span className={'text-supply'}>фазе Снабжения</span> крысолюды могут
      нанимать пехоту {unit} прямо на <b>свободную рабочую ферму</b> {farm}: отряд не стоит монет {coins} и
      обходится в <b>1 единицу снабжения</b> {supply}. Новобранец сразу встает на ферму, а размер {size} 1 получает
      вместе с итогом снабжения: в сетевой игре — когда фазу Снабжения завершат все игроки, в локальной — в конце
      хода крысолюдов. Одновременно таких отрядов может быть не больше четырёх, а в последнем раунде партии
      нанимать уже нельзя. Отмена снабжения такого новобранца убирает и сам отряд — найм и снабжение здесь одно
      действие.
    </p>
    <p>
      Пока итог снабжения не применен, новобранец противникам не мешает: не блокирует их строения и не
      перекрывает пути снабжения. В сетевой игре так до конца фазы Снабжения у всех, а в локальной после хода
      крысолюдов новобранец уже блокирует фермы и пути игроков, которые ходят позже.
    </p>
    <p>
      <Subheading>Дополнительное снабжение растит размер.</Subheading> У всех фракций дополнительное снабжение размер не
      увеличивает, а пехоте крысолюдов {unit}{heavyInfantry}{eliteUnit} оно вдобавок к обычному бонусу даёт <b>+1
      к размеру</b> {size}. Катапульты {catapult} и кавалерия {lightCavalry}{heavyCavalry} этот прирост не получают —
      им дополнительное снабжение даёт только обычный бонус фазы Войны (переброс костей урона, дополнительное
      перемещение или манёвр), как и всем остальным.
    </p>
    <p>
      <Subheading>Сила в числе.</Subheading> В ближнем бою отряд крысолюдов получает <b>+1 к урону</b> {damage} <b>за каждый
      свой отряд на соседних ячейках</b> — держать строй выгодно. Как и любые бонусы урона, суммарная прибавка
      не может превысить размер {size} самого отряда.
    </p>
  </>
);

const SuccubesRules = () => (
  <>
    <p>
      <Subheading>Соблазнение.</Subheading> На <span className={'text-war'}>этапе Способности</span> (между
      Перемещением и Стрельбой) отряд {unit} суккубов может попытаться захватить под свой контроль любой чужой
      отряд {unitEnemy} — вражеский, нейтральный или уже захваченный другим игроком-суккубом — на расстоянии до
      {distance}{SUCCUBES_SEDUCTION_RANGE} ячеек. Как и у стрельбы эльфов, нужна прямая видимость: обзор
      перекрывают горы {mountain} и достроенные крепости. А <span className={'text-war'}>вражеский отряд на соседней ячейке мешает</span> соблазнить
      так же, как мешает стрелять. Катапульты {catapult} соблазнять не умеют.
    </p>
    <p>
      <Subheading>Бросок и цена.</Subheading> Итог — {dice}d20 плюс размер {size} своего отряда минус размер {size}
      отряда цели; успех при результате <b>{SUCCUBES_SEDUCTION_SUCCESS_THRESHOLD} и больше</b>. Попытка стоит
      {coins}{SUCCUBES_SEDUCTION_COST} монеты и списывается <b>независимо от исхода</b>. Каждый отряд суккубов может
      попытаться соблазнить не чаще одного раза за этот этап, а само действие, как стрельба и ближний бой, отмене
      {undo} не подлежит.
    </p>
    <p>
      <Subheading>Отряд сохраняет свою фракцию.</Subheading> При успехе меняется только владелец — исходная фракция
      отряда остаётся прежней, а с ней и все её бонусы и ограничения. Например, захваченный Великан {eliteUnit}
      продолжает расти в снабжении {supply} без предела размера {size}, как и до захвата, — суккуб получает не
      «обычную пехоту», а полноценного Великана под своим флагом. На поле такой отряд отображается двумя цветами:
      левая половина силуэта — цвет суккуба, правая — цвет прежнего владельца (нейтрального — если отряд был
      ничей).
    </p>
    <p>
      <Subheading>Успевает в этот же ход.</Subheading> Способности идут раньше Стрельбы и Ближнего боя в порядке
      этапов фазы Войны, поэтому только что соблазнённый отряд не ждёт следующего раунда: если он ещё не стрелял и
      не дрался в этом ходу, он может сделать это прямо сейчас вместе с остальной армией суккуба.
    </p>
  </>
);

const GoblinsRules = () => (
  <>
    <p>
      <Subheading>Ловушки.</Subheading> На этапе <span className={'text-war'}>Способности</span> отряд {unit}
      гоблинов численностью {GOBLINS_MIN_UNIT_SIZE_FOR_ABILITY} и больше может установить ловушку на гекс в радиусе
      {distance}{GOBLINS_ABILITY_RADIUS} ячеек за {coins}{GOBLINS_TRAP_SETUP_COST} монету (списывается независимо от
      исхода). Бросок {dice}d6, успех при <b>{GOBLINS_ABILITY_SUCCESS_THRESHOLD} и больше</b>. Нельзя ставить на
      гексы гор {mountain}, смежно вражескому отряду {unitEnemy} или на гекс со своей же ловушкой — ловушки других
      игроков-гоблинов не мешают. Ловушка невидима для всех, включая других гоблинов: остальным игрокам виден
      только сам факт попытки, без её местоположения.
    </p>
    <p>
      <Subheading>Срабатывание.</Subheading> Ловушка активируется, когда вражеский отряд {unitEnemy} оказывается
      смежно её гексу в процессе своего перемещения — отряд немедленно получает случайно от 1 до 3 единиц урона
      {damage}, а сама ловушка исчезает. Если за одно перемещение отряд заденет несколько ловушек, урон каждой
      суммируется, и отряд может погибнуть, не дойдя до конечной точки маршрута; такое перемещение отменить {undo}
      нельзя. Убийство ловушкой засчитывается как обычное; если сработали ловушки нескольких игроков-гоблинов, очки
      получает тот, чей суммарный урон оказался больше (при равенстве — у кого сработало больше ловушек, иначе — чья
      очередь хода наступает раньше).
    </p>
    <p>
      <Subheading>Снятие ловушки.</Subheading> Вместо установки новой отряд гоблинов может снять свою ранее
      поставленную ловушку в том же радиусе {distance}{GOBLINS_ABILITY_RADIUS} — бесплатно, без броска кубика, и
      получить за это {coins}{GOBLINS_TRAP_REMOVAL_REWARD} монету.
    </p>
    <p>
      <Subheading>Разрушение строений.</Subheading> Третья способность — бесплатная попытка повредить вражеское
      строение {specialBuilding} в радиусе {distance}{GOBLINS_ABILITY_RADIUS}: {dice}d6, успех при <b>
      {GOBLINS_ABILITY_SUCCESS_THRESHOLD} и больше</b>, повторное попадание разрушает уже повреждённое строение —
      как у катапульт. Крепость вместо этого получает результат броска минус {GOBLINS_FORTRESS_DAMAGE_PENALTY} урона
      (при броске 6 — 4 урона, при 1-2 — урона нет вовсе). Вражеский отряд {unitEnemy}, стоящий на гексе строения,
      урона не получает — в отличие от катапульт, которые задевают его тем же выстрелом.
    </p>
  </>
);

const CelectialsRules = () => (
  <>
    <p>
      <Subheading>Полет.</Subheading> Все отряды {unit} селестиалов летают {flight}: промежуточные ячейки пути для них
      ничего не ограничивают — подробнее в разделе{' '}
      <RulesSectionLink sectionId={RULES_SECTION_IDS.WAR_PHASE} anchorId={RULES_ANCHOR_IDS.FLYING_UNITS}>
        «Фаза Войны» — летающие отряды
      </RulesSectionLink>.
    </p>
    <p>
      <Subheading>Удвоенные кости урона.</Subheading> В ближнем бою — и на своем, и на вражеском этапе ближнего
      боя — каждый отряд селестиалов бросает {dice}{CELECTIALS_MELEE_DAMAGE_DICE_COUNT} кости урона {damage} вместо
      двух, и в расчет урона идут {MELEE_COUNTED_DAMAGE_DICE_COUNT} лучшие. За дополнительное снабжение {supply}
      можно перебросить любые из четырех костей, после чего лучшие определяются заново. Стрельба катапульт
      {catapult} селестиалов — как у всех.
    </p>
  </>
);

export const FACTION_DETAILED_RULES = {
  [FACTIONS.HUMANS]: HumansRules,
  [FACTIONS.ORCS]: OrcsRules,
  [FACTIONS.DWARVES]: DwarvesRules,
  [FACTIONS.VENTORY]: VentoryRules,
  [FACTIONS.LEPRECHAUNS]: LeprechaunsRules,
  [FACTIONS.DEMONS]: DemonsRules,
  [FACTIONS.BEASTMEN]: BeastmenRules,
  [FACTIONS.ELEMENTALS]: ElementalsRules,
  [FACTIONS.ELVES]: ElvesRules,
  [FACTIONS.ENLIGHTENED]: EnlightenedRules,
  [FACTIONS.GIANTS]: GiantsRules,
  [FACTIONS.INSECTOIDS]: InsectoidsRules,
  [FACTIONS.RATMEN]: RatmenRules,
  [FACTIONS.SUCCUBES]: SuccubesRules,
  [FACTIONS.GOBLINS]: GoblinsRules,
  [FACTIONS.CELECTIALS]: CelectialsRules,
};

export default FACTION_DETAILED_RULES;
